const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

type ShopifyGlobal = {
  idToken(): Promise<string>;
};

function getShopify(): ShopifyGlobal {
  const value = (globalThis as typeof globalThis & { shopify?: ShopifyGlobal }).shopify;
  if (!value) {
    throw new Error('Shopify App Bridge is not available. Open Atellius B2B inside Shopify Admin.');
  }
  return value;
}

async function fetchWithIdToken(path: string, init: RequestInit): Promise<Response> {
  const token = await getShopify().idToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });
}

export async function authenticatedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured.');
  }

  const response = await fetchWithIdToken(path, init);
  if (response.status !== 401) return response;

  // Shopify ID tokens are intentionally short lived. When a request races token expiry
  // (or a just-issued token is a fraction ahead of the API host clock), obtain another
  // App Bridge token and retry exactly once. Never loop authentication retries.
  await new Promise((resolve) => window.setTimeout(resolve, 250));
  return fetchWithIdToken(path, init);
}

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `${fallback} (${response.status})`);
  }
  return (await response.json()) as T;
}

export type BootstrapResult = {
  tenant_id: string;
  shop: {
    id: string;
    name: string | null;
    domain: string;
    currency: string | null;
  };
  core_entitlement: string;
};

export type SyncRun = {
  id: string;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  records_seen: number;
  started_at: string;
  completed_at: string | null;
  error: string | null;
};

export type SyncQueue = {
  id: string;
  status: 'queued' | 'running';
  attempts: number;
  created_at: string;
  locked_at: string | null;
};

export type SyncStatusResult = {
  installed: boolean;
  queue: SyncQueue | null;
  sync: SyncRun | null;
};

export type CapabilityResult = {
  installed: boolean;
  capabilities: Record<string, unknown> | null;
};

export type HealthFinding = {
  check_code: string;
  category: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  recommendation: string;
  affected_count: number;
  score_penalty: number;
  module_code: string | null;
  evidence: Record<string, unknown>;
};

export type ModuleRecommendation = {
  code: string;
  finding_count: number;
  critical_count: number;
  warning_count: number;
  score_impact: number;
};

export type HealthReport = {
  id: string;
  score: number;
  status: 'excellent' | 'good' | 'needs_attention' | 'at_risk';
  category_scores: Record<string, number>;
  summary: Record<string, number | string>;
  evaluated_at: string;
  findings: HealthFinding[];
  recommended_modules: ModuleRecommendation[];
};

export type HealthScorePoint = {
  score: number;
  status: string;
  evaluated_at: string;
};

export type HealthResult = {
  installed: boolean;
  ready: boolean;
  report_current: boolean;
  sync_status?: string | null;
  queue: SyncQueue | null;
  history: HealthScorePoint[];
  score_delta: number | null;
  report: HealthReport | null;
};

export type RuleConditionInput = {
  field: string;
  operator: string;
  value: unknown;
  sequence: number;
};

export type RuleActionInput = {
  kind: string;
  value: Record<string, unknown>;
  sequence: number;
};

export type PolicyDraftInput = {
  name: string;
  description?: string | null;
  priority: number;
  valid_from?: string | null;
  valid_to?: string | null;
  conditions: RuleConditionInput[];
  actions: RuleActionInput[];
};

export type PolicySummary = {
  id: string;
  policy_key: string;
  name: string;
  description: string | null;
  status: string;
  priority: number;
  draft_version: number | null;
  published_version: number | null;
  updated_at: string;
};

export type PolicyDraft = PolicyDraftInput & {
  id: string;
  policy_key: string;
  status: string;
  draft_version: number;
};

export type SimulationContext = {
  company_id?: string | null;
  company_location_id?: string | null;
  catalog_id?: string | null;
  product_id?: string | null;
  variant_id?: string | null;
  quantity: number;
  currency: string;
  base_price: string;
};

export type RuleSimulationResult = {
  base_price: string;
  final_price: string;
  currency: string;
  min_quantity: number | null;
  max_quantity: number | null;
  quantity_increment: number | null;
  price_source_policy: string | null;
  matched_policies: string[];
  safe_to_publish: boolean;
  rules_considered: number;
  conflicts: Array<{
    family: string;
    priority: number;
    policy_keys: string[];
    message: string;
  }>;
  trace: Array<{
    policy_key: string;
    version_number: number;
    priority: number;
    matched: boolean;
    reason: string;
    applied_actions: string[];
    ignored_actions: string[];
  }>;
  metadata: Record<string, unknown>;
};

export async function bootstrapShopify(): Promise<BootstrapResult> {
  const response = await authenticatedFetch('/api/shopify/bootstrap', { method: 'POST' });
  return jsonOrError<BootstrapResult>(response, 'Shopify bootstrap failed');
}

export async function enqueueShopifySync(): Promise<{ status: string; job_id: string | null }> {
  const response = await authenticatedFetch('/api/shopify/sync', { method: 'POST' });
  return jsonOrError(response, 'Could not queue Shopify sync');
}

export async function getShopifySyncStatus(): Promise<SyncStatusResult> {
  const response = await authenticatedFetch('/api/shopify/sync/status');
  return jsonOrError(response, 'Could not read Shopify sync status');
}

export async function getShopifyCapabilities(): Promise<CapabilityResult> {
  const response = await authenticatedFetch('/api/shopify/capabilities');
  return jsonOrError(response, 'Could not read Shopify capabilities');
}

export async function getLatestHealthReport(): Promise<HealthResult> {
  const response = await authenticatedFetch('/api/health/latest');
  return jsonOrError(response, 'Could not read B2B Health Check');
}

export async function enqueueHealthCheck(): Promise<{ status: string; job_id: string | null }> {
  const response = await authenticatedFetch('/api/health/run', { method: 'POST' });
  return jsonOrError(response, 'Could not queue B2B Health Check');
}

export async function getPolicies(): Promise<PolicySummary[]> {
  const response = await authenticatedFetch('/api/policies');
  const body = await jsonOrError<{ policies: PolicySummary[] }>(response, 'Could not load policies');
  return body.policies;
}

export async function getPolicyDraft(policyId: string): Promise<PolicyDraft> {
  const response = await authenticatedFetch(`/api/policies/${policyId}/draft`);
  return jsonOrError(response, 'Could not load policy draft');
}

export async function createPolicyDraft(
  input: PolicyDraftInput,
): Promise<{ id: string; policy_key: string; draft_version: number; status: string }> {
  const response = await authenticatedFetch('/api/policies', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not create policy draft');
}

export async function updatePolicyDraft(
  policyId: string,
  input: PolicyDraftInput,
): Promise<{ id: string; policy_key: string; draft_version: number; status: string }> {
  const response = await authenticatedFetch(`/api/policies/${policyId}/draft`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return jsonOrError(response, 'Could not update policy draft');
}

export async function simulateRules(
  context: SimulationContext,
  rules: Array<PolicyDraftInput & { policy_key: string; version_number: number }>,
): Promise<RuleSimulationResult> {
  const response = await authenticatedFetch('/api/rules/simulate', {
    method: 'POST',
    body: JSON.stringify({ context, rules }),
  });
  return jsonOrError(response, 'Could not simulate commercial rules');
}
